import { useCallback, useEffect, useRef, useState } from 'react';

import i18n from '../../locales/i18n';
import { ActionTargetReference } from '../../types/actionTypes';
import { fetchRtuConfigurationFiles, fetchRtuDeploymentFiles } from '../../services/rtuService';

export type RtuFileListState = 'idle' | 'loading' | 'ready' | 'error';

export const RTU_FILE_LIST_CREDENTIALS_UNAVAILABLE = 'RTU_FILE_LIST_CREDENTIALS_UNAVAILABLE';

/** Backend error code -> i18n key under actionsModal.download.fileListErrors. */
const ERROR_MESSAGE_KEYS: Record<string, string> = {
  RTU_FILE_LIST_UNREACHABLE: 'unreachable',
  RTU_ACTION_BUSY: 'busy',
  RTU_FILE_LIST_REJECTED: 'rejected',
  RTU_FILE_LIST_SESSION_EXPIRED: 'signIn',
  RTU_FILE_LIST_MALFORMED: 'unusable',
  RTU_FILE_LIST_HTTP_ERROR: 'unusable',
  RTU_FILE_LIST_EMPTY_RESPONSE: 'unusable',
  [RTU_FILE_LIST_CREDENTIALS_UNAVAILABLE]: 'credentials',
};

/** Codes whose message is self-explanatory: no support reference is shown. */
const CODES_WITHOUT_REFERENCE = new Set(['RTU_FILE_LIST_UNREACHABLE', 'RTU_ACTION_BUSY']);

/**
 * Loads the RTU-resident file list (WLRtuWebServices v1.06 §4.6) for the
 * "existing file" download flow, on explicit request only.
 *
 * Reading this list costs a full login/list/logout round trip against the
 * RTU, and the RTU repository can change between requests, so this
 * deliberately never auto-fetches and never caches a previous result: the
 * caller must call `load()`, and every call reaches the RTU again.
 */
export const useRtuFileList = (kind: 'config' | 'deployment', targets: ActionTargetReference[], enabled: boolean) => {
  const [files, setFiles] = useState<string[]>([]);
  const [state, setState] = useState<RtuFileListState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const generationRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
    // Identity contract: Well ID + serial is the operational target.
    // Two Well IDs sharing one physical serial must not share file-list state.
    const targetKey = JSON.stringify(targets.map((target) => ({ rtuId: target.rtuId, serialNo: target.serialNo })));

  // A stale list from a different RTU/target set must never be shown as if
  // it were current. Clears state without ever fetching on its own.
  useEffect(() => {
    generationRef.current += 1;
    abortRef.current?.abort();
    setFiles([]);
    setError(null);
    setErrorCode(null);
    setRequestId(null);
    setState('idle');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, kind, targetKey]);

  const load = useCallback(() => {
    if (!enabled || targets.length !== 1) {
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const generation = generationRef.current + 1;
    generationRef.current = generation;

    setError(null);
    setErrorCode(null);
    setRequestId(null);
    setState('loading');
    const fetcher = kind === 'deployment' ? fetchRtuDeploymentFiles : fetchRtuConfigurationFiles;
    void fetcher(targets, controller.signal)
      .then((response) => {
        if (generation !== generationRef.current) return;
        setFiles(Array.isArray(response.files) ? response.files : []);
        setState('ready');
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted || generation !== generationRef.current) return;
        const failure = requestError as {
          code?: string;
          response?: { data?: { code?: string; message?: string; requestId?: string } };
        };
        const body = failure?.response?.data;
        const timedOut = failure?.code === 'ECONNABORTED' || failure?.code === 'ETIMEDOUT';
        const backendCode = typeof body?.code === 'string' ? body.code : null;
        const messageKey = backendCode ? ERROR_MESSAGE_KEYS[backendCode] : undefined;
        const backendRequestId = typeof body?.requestId === 'string' && body.requestId ? body.requestId : null;
        setError(
          messageKey
            ? i18n.t(`actionsModal.download.fileListErrors.${messageKey}`)
            : body?.message
              || i18n.t(timedOut ? 'actionsModal.download.filesLoadTimeout' : 'actionsModal.download.filesLoadFailed')
        );
        setErrorCode(backendCode);
        setRequestId(!timedOut && !(backendCode && CODES_WITHOUT_REFERENCE.has(backendCode)) ? backendRequestId : null);
        setState('error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, kind, targetKey]);

  return { error, errorCode, files, load, requestId, state };
};
